/**
 * Settings Panel Component
 * Global settings UI with real-time updates
 */

import { settings } from '../utils/settings.js';
import { i18n, t } from '../i18n.js';

export const settingsPanel = () => ({
  // State
  showPanel: false,
  currentSettings: {},
  isDirty: false,
  
  // Initialize component
  init() {
    console.log('Settings panel initialized');
    
    // Load current settings
    this.loadSettings();
    
    // Subscribe to settings changes
    this.unsubscribe = settings.subscribe((key, newValue, oldValue) => {
      this.currentSettings[key] = newValue;
      
      // Update i18n if language changed
      if (key === 'language') {
        i18n.setLanguage(newValue);
        // Force Alpine to re-evaluate text content
        this.$nextTick(() => {
          window.dispatchEvent(new CustomEvent('language-changed', { 
            detail: { language: newValue } 
          }));
        });
      }
    });
    
    // Listen for global language changes
    window.addEventListener('language-changed', () => {
      this.$nextTick(() => {
        // Force re-render of reactive text
        this.loadSettings();
      });
    });
  },
  
  // Load current settings
  loadSettings() {
    this.currentSettings = settings.getAll();
  },
  
  // Open settings panel
  openPanel() {
    this.showPanel = true;
    this.loadSettings();
    this.isDirty = false;
    
    // Focus on panel for accessibility
    this.$nextTick(() => {
      const panel = this.$refs.settingsPanel;
      if (panel) {
        panel.focus();
      }
    });
  },
  
  // Close settings panel
  closePanel() {
    this.showPanel = false;
    this.isDirty = false;
  },
  
  // Handle audio toggle
  toggleAudio() {
    const newValue = !this.currentSettings.audioEnabled;
    settings.set('audioEnabled', newValue);
    this.markDirty();
  },
  
  // Handle theme change
  changeTheme(theme) {
    if (['light', 'dark', 'system'].includes(theme)) {
      settings.set('theme', theme);
      this.markDirty();
    }
  },
  
  // Handle language change
  changeLanguage(language) {
    if (['es', 'en'].includes(language)) {
      settings.set('language', language);
      this.markDirty();
    }
  },
  
  // Handle text size toggle
  toggleTextLarge() {
    const newValue = !this.currentSettings.textLarge;
    settings.set('textLarge', newValue);
    this.markDirty();
  },
  
  // Handle contrast toggle
  toggleContrastHigh() {
    const newValue = !this.currentSettings.contrastHigh;
    settings.set('contrastHigh', newValue);
    this.markDirty();
  },
  
  // Reset all settings
  resetSettings() {
    if (confirm(t('settings.resetConfirm'))) {
      settings.reset();
      this.loadSettings();
      this.isDirty = false;
      
      // Show notification
      if (this.$store && this.$store.app && this.$store.app.addNotification) {
        this.$store.app.addNotification(
          t('settings.resetSuccess', 'Ajustes restablecidos'), 
          'success'
        );
      }
    }
  },
  
  // Mark settings as changed
  markDirty() {
    this.isDirty = true;
  },
  
  // Handle panel backdrop click
  handleBackdropClick(event) {
    if (event.target === event.currentTarget) {
      this.closePanel();
    }
  },
  
  // Handle escape key
  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closePanel();
    }
  },
  
  // Get theme options for select
  getThemeOptions() {
    return [
      { value: 'light', label: t('settings.theme.light') },
      { value: 'dark', label: t('settings.theme.dark') },
      { value: 'system', label: t('settings.theme.system') }
    ];
  },
  
  // Get language options for select
  getLanguageOptions() {
    return [
      { value: 'es', label: t('settings.language.es') },
      { value: 'en', label: t('settings.language.en') }
    ];
  },
  
  // Get current theme display name
  getCurrentThemeLabel() {
    const theme = this.currentSettings.theme;
    return t(`settings.theme.${theme}`, theme);
  },
  
  // Get current language display name
  getCurrentLanguageLabel() {
    const language = this.currentSettings.language;
    return t(`settings.language.${language}`, language);
  },
  
  // Cleanup
  destroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  },
  
  // Reactive getters for Alpine templates
  get isAudioEnabled() {
    return this.currentSettings.audioEnabled;
  },
  
  get currentTheme() {
    return this.currentSettings.theme;
  },
  
  get currentLanguage() {
    return this.currentSettings.language;
  },
  
  get isTextLarge() {
    return this.currentSettings.textLarge;
  },
  
  get isContrastHigh() {
    return this.currentSettings.contrastHigh;
  },
  
  // Text helpers that react to language changes
  getText(key, fallback = null) {
    return t(key, fallback);
  }
});